<template>
  <b-container>
    <b-card
      border-variant="danger"
      :header="$t('PaymentOfflineProcess.header')"
      header-bg-variant="danger"
      header-text-variant="white"
      align="left"
    >
      <b-row>
        <b-table responsive outlined striped hover :items="cartItems" :fields="fields">
          <template v-slot:cell(namesize)="data">
            {{data.item.name}} {{data.item.size}}
            <p v-if="data.item.style">{{ $t('PaymentProcess.style') }}: {{data.item.style}}</p>
            <p
              v-if="data.item.extras.length > 0"
            >{{ $t('PaymentProcess.extras') }}: {{data.item.extras.join(', ')}}</p>
          </template>

          <template
            v-slot:cell(pricecurrency)="data"
          >{{data.item.price.toFixed(2)}} {{data.item.currency}}</template>
        </b-table>
      </b-row>

      <b-row align-v="end" class="float-right">
        <b-col>
          <b>{{ $t('PaymentProcess.total') }}:&nbsp;{{ cartAmount.total.toFixed(2) }}&nbsp;{{ cartAmount.currency }}</b>
        </b-col>
      </b-row>

      <b-row align-v="end" class="float-left">
        <b-col>
          {{stripeToken.token.card.name}}
          <br />
          ({{stripeToken.token.card.brand}}&nbsp;...{{stripeToken.token.card.last4}})
        </b-col>
      </b-row>
    </b-card>
    <div>
      <b-row class="mt-1" align-v="end">
        <b-col>
          <span>
            <b-form-checkbox
              class="float-right"
              v-model="acceptedConditions"
              style="margin-left: 5px;"
            />
            <a
              href="#"
              class="float-right"
              @click="showModal"
            >{{ $t('PaymentProcess.termsConditions') }}</a>
          </span>
        </b-col>
      </b-row>
      <b-row class="mt-3" align-v="end">
        <b-col>
          <b-button
            variant="secondary"
            class="float-left"
            @click="cancelOrder"
          >{{ $t('PaymentProcess.cancel') }}</b-button>
        </b-col>
        <b-col>
          <b-button
            variant="danger"
            class="float-right"
            :disabled="!acceptedConditions"
            @click="processPayment"
          >{{ $t('PaymentProcess.pay') }}</b-button>
        </b-col>
      </b-row>
      <b-row class="mt-3 justify-content-center">
        <b-alert :show="error.received" variant="danger" class="text-center">{{error.msg}}</b-alert>
      </b-row>
    </div>

    <!-- CONDITIONS MODAL -->
    <b-modal ref="conditionsModalRef" hide-footer header-bg-variant="danger">
      <div class="d-block text-center">
        <TermsConditions />
      </div>
      <b-btn class="mt-3" cancel @click="acceptConditions(false)">Cancel</b-btn>
      <b-btn class="mt-3" ok variant="danger" @click="acceptConditions(true)">Agree</b-btn>
    </b-modal>
  </b-container>
</template>

<script>
import TermsConditions from '@/components/TermsConditions'
import PaymentService from '@/services/PaymentService'
import ItemsService from '@/services/ItemsService'
import PrintService from '@/services/PrintService'
// import OrdersService from '@/services/OrdersService'
import HelperFunctions from '@/extras/HelperFunctions'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'PaymentProcess',
  components: {
    TermsConditions
  },
  data () {
    return {
      fields: [
        {
          key: 'namesize',
          label: this.$i18n.t('PaymentProcess.itemName')
        },
        {
          key: 'quantity',
          label: this.$i18n.t('PaymentProcess.itemQuantity')
        },
        {
          key: 'pricecurrency',
          label: this.$i18n.t('PaymentProcess.itemPrice')
        }
      ],
      order: null,
      acceptedConditions: false,
      error: {
        received: false,
        msg: ''
      }
    }
  },
  mounted () {
    // redirect to cart if stripeToken is empty
    if (!this.stripeToken) this.$router.push({ name: 'Payment' })
  },
  methods: {
    cancelOrder () {
      const token = null
      const cartAmount = null
      // take out store info
      this.$store.dispatch('setStripeToken', token)
      this.$store.dispatch('setCartAmount', cartAmount)
      this.clearCart()

      // navigate back to main page
      this.$router.push({ name: 'Home' })
    },
    async processPayment () {
      try {
        const response = await PaymentService.processPayment({
          token: this.stripeToken.token,
          cartAmount: this.cartAmount,
          stripeAccount: this.stripeConnectUserId
        })

        // save order in DB
        this.saveOrder(response.data.charge)
      } catch (error) {
        this.error.received = true
        this.error.msg = error.response.data.error
      }
    },
    async saveOrder (charge) {
      const order = {
        orderConfId: HelperFunctions.randomString(),
        tableNumber: this.tableNumber,
        name: charge.source.name,
        items: this.cartItems,
        amount: charge.amount / 100,
        currency: charge.currency,
        created: charge.created * 1000,
        stripeId: charge.id,
        paid: charge.paid,
        brand: charge.source.brand,
        last4: charge.source.last4,
        stripe_status: charge.status,
        payment_type: 'online',
        order_status: 'new'
      }
      try {
        const orderinfo = await PaymentService.saveOrder(order)

        // UNCOMMENT WHEN READY FOR PRINTING
        /* try {
          const savedOrder = await OrdersService.getOrder({ _id: orderinfo.data.orderinfo._id })
          // build order for printing
          this.buildPrintOrder(savedOrder)
        } catch (error) {
          console.log(error)
        } */

        // take out store info
        this.$store.dispatch('setStripeToken', null)
        this.$store.dispatch('setCartAmount', null)
        this.$store.dispatch('setTableNumber', null)
        this.clearCart()

        // navigate to payment success page
        this.$router.push({
          name: 'PaymentSuccess',
          params: { orderinfo: orderinfo }
        })
      } catch (error) {
        console.log(error)
      }
    },
    async buildPrintOrder (order) {
      const orderedItems = order.data.orderinfo.items
      let fetchedItems = []

      // loop through items
      for (const i of orderedItems) {
        let newItem = {
          _id: i._id,
          category: i.category,
          currency: i.currency,
          price: i.price,
          quantity: i.quantity,
          name: null,
          extras: [],
          variant: null,
          style: null
        }
        const response = await ItemsService.getItemById(i._id)
        if (response.data) {
          newItem.name = this.filterName(response.data)
          response.data.styles.forEach(s => {
            if (
              s.name.some(n => {
                return n.value === i.style
              })
            ) {
              newItem.style = this.filterName(s)
            }
          })
          response.data.extras.forEach(e => {
            if (
              e.name.some(n => {
                return i.extras.some(ex => {
                  return ex === n.value
                })
              })
            ) {
              let extra = this.filterName(e)
              newItem.extras.push(extra)
            }
          })
          fetchedItems.push(newItem)
        } else {
          const response2 = await ItemsService.getItemByVariantId({
            _id: i._id
          })

          newItem.name = this.filterName(response2.data)
          response2.data.styles.forEach(s => {
            if (
              s.name.some(n => {
                return n.value === i.style
              })
            ) {
              newItem.style = this.filterName(s)
            }
          })
          response2.data.extras.forEach(e => {
            if (
              e.name.some(n => {
                return i.extras.some(ex => {
                  return ex === n.value
                })
              })
            ) {
              let extra = this.filterName(e)
              newItem.extras.push(extra)
            }
          })
          response2.data.variants.forEach(v => {
            if (
              v.name.some(n => {
                return n.value === i.variant
              })
            ) {
              newItem.variant = this.filterName(v)
            }
          })
          fetchedItems.push(newItem)
        }
      }

      // assign newly formed items to orderinfo.items
      order.data.orderinfo.items = fetchedItems

      // send to printer
      /* UNCOMMENT WHEN READY FOR PRINTING */
      // this.printOrder(order)
    },
    async printOrder (order) {
      try {
        await PrintService.print(order)
      } catch (err) {
        console.log(err)
      }
    },
    filterName (item) {
      // filter for selected language
      let res1 = item.name.filter(l => {
        return l.lang === this.printerLanguage
      })
      // if res1 value undefined, filter for and assign a defined value
      let res2 =
        res1.length > 0
          ? res1[0].value
          : item.name.filter(l => {
            return l.value.length > 0
          })[0].value
      // if no value found at all, return null
      return res2.length > 0 ? res2 : null
    },
    ...mapActions(['clearCart']),
    showModal () {
      this.$refs.conditionsModalRef.show()
    },
    hideModal () {
      this.$refs.conditionsModalRef.hide()
    },
    acceptConditions (decision) {
      if (decision === true) this.acceptedConditions = true
      else this.acceptedConditions = false

      this.hideModal()
    }
  },
  computed: {
    ...mapGetters({
      cartItems: 'cartItems',
      cartAmount: 'cartAmount',
      stripeToken: 'stripeToken',
      tableNumber: 'tableNumber',
      stripeConnectUserId: 'stripeConnectUserId',
      printerLanguage: 'printerLanguage'
    })
  }
}
</script>
<style scoped>
.custom-control-inline {
  margin-right: 0;
}
p {
  margin-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/PaymentProcess.vue